// Dependency Injection Container for Reco feature
// Ensures single instances of repositories and use cases

import { CreditsRepositoryImpl } from "../data/repositories/CreditsRepositoryImpl";
import { BankPaymentsRepositoryImpl } from "../data/repositories/BankPaymentsRepositoryImpl";
import { GetCustomerCreditsSummaryUseCase } from "../domain/usecases/GetCustomerCreditsSummaryUseCase";
import { GetCustomerCreditsListUseCase } from "../domain/usecases/GetCustomerCreditsListUseCase";
import { GetCustomerOutstandingInvoicesUseCase } from "../domain/usecases/GetCustomerOutstandingInvoicesUseCase";
import { RefreshCustomerCreditsCacheUseCase } from "../domain/usecases/RefreshCustomerCreditsCacheUseCase";
import { GetVendorCreditsSummaryUseCase } from "../domain/usecases/GetVendorCreditsSummaryUseCase";
import { GetVendorCreditsListUseCase } from "../domain/usecases/GetVendorCreditsListUseCase";
import { GetVendorOutstandingBillsUseCase } from "../domain/usecases/GetVendorOutstandingBillsUseCase";
import { RefreshVendorCreditsCacheUseCase } from "../domain/usecases/RefreshVendorCreditsCacheUseCase";
import { GetBankPaymentsSummaryUseCase } from "../domain/usecases/GetBankPaymentsSummaryUseCase";

class RecoContainer {
  // Singleton repositories
  private creditsRepository: CreditsRepositoryImpl | null = null;
  private bankPaymentsRepository: BankPaymentsRepositoryImpl | null = null;

  // Singleton use cases
  private getCustomerCreditsSummaryUseCase: GetCustomerCreditsSummaryUseCase | null =
    null;
  private getCustomerCreditsListUseCase: GetCustomerCreditsListUseCase | null =
    null;
  private getCustomerOutstandingInvoicesUseCase: GetCustomerOutstandingInvoicesUseCase | null =
    null;
  private refreshCustomerCreditsCacheUseCase: RefreshCustomerCreditsCacheUseCase | null =
    null;
  private getVendorCreditsSummaryUseCase: GetVendorCreditsSummaryUseCase | null =
    null;
  private getVendorCreditsListUseCase: GetVendorCreditsListUseCase | null =
    null;
  private getVendorOutstandingBillsUseCase: GetVendorOutstandingBillsUseCase | null =
    null;
  private refreshVendorCreditsCacheUseCase: RefreshVendorCreditsCacheUseCase | null =
    null;
  private getBankPaymentsSummaryUseCase: GetBankPaymentsSummaryUseCase | null =
    null;

  // Get or create Credits Repository
  private getCreditsRepository(): CreditsRepositoryImpl {
    if (!this.creditsRepository) {
      this.creditsRepository = new CreditsRepositoryImpl();
    }
    return this.creditsRepository;
  }

  // Get or create Bank Payments Repository
  private getBankPaymentsRepository(): BankPaymentsRepositoryImpl {
    if (!this.bankPaymentsRepository) {
      this.bankPaymentsRepository = new BankPaymentsRepositoryImpl();
    }
    return this.bankPaymentsRepository;
  }

  // Customer Credits Use Cases
  getCustomerCreditsSummary(): GetCustomerCreditsSummaryUseCase {
    if (!this.getCustomerCreditsSummaryUseCase) {
      this.getCustomerCreditsSummaryUseCase =
        new GetCustomerCreditsSummaryUseCase(this.getCreditsRepository());
    }
    return this.getCustomerCreditsSummaryUseCase;
  }

  getCustomerCreditsList(): GetCustomerCreditsListUseCase {
    if (!this.getCustomerCreditsListUseCase) {
      this.getCustomerCreditsListUseCase = new GetCustomerCreditsListUseCase(
        this.getCreditsRepository()
      );
    }
    return this.getCustomerCreditsListUseCase;
  }

  getCustomerOutstandingInvoices(): GetCustomerOutstandingInvoicesUseCase {
    if (!this.getCustomerOutstandingInvoicesUseCase) {
      this.getCustomerOutstandingInvoicesUseCase =
        new GetCustomerOutstandingInvoicesUseCase(this.getCreditsRepository());
    }
    return this.getCustomerOutstandingInvoicesUseCase;
  }

  refreshCustomerCreditsCache(): RefreshCustomerCreditsCacheUseCase {
    if (!this.refreshCustomerCreditsCacheUseCase) {
      this.refreshCustomerCreditsCacheUseCase =
        new RefreshCustomerCreditsCacheUseCase(this.getCreditsRepository());
    }
    return this.refreshCustomerCreditsCacheUseCase;
  }

  // Vendor Credits Use Cases
  getVendorCreditsSummary(): GetVendorCreditsSummaryUseCase {
    if (!this.getVendorCreditsSummaryUseCase) {
      this.getVendorCreditsSummaryUseCase = new GetVendorCreditsSummaryUseCase(
        this.getCreditsRepository()
      );
    }
    return this.getVendorCreditsSummaryUseCase;
  }

  getVendorCreditsList(): GetVendorCreditsListUseCase {
    if (!this.getVendorCreditsListUseCase) {
      this.getVendorCreditsListUseCase = new GetVendorCreditsListUseCase(
        this.getCreditsRepository()
      );
    }
    return this.getVendorCreditsListUseCase;
  }

  getVendorOutstandingBills(): GetVendorOutstandingBillsUseCase {
    if (!this.getVendorOutstandingBillsUseCase) {
      this.getVendorOutstandingBillsUseCase = new GetVendorOutstandingBillsUseCase(
        this.getCreditsRepository()
      );
    }
    return this.getVendorOutstandingBillsUseCase;
  }

  refreshVendorCreditsCache(): RefreshVendorCreditsCacheUseCase {
    if (!this.refreshVendorCreditsCacheUseCase) {
      this.refreshVendorCreditsCacheUseCase =
        new RefreshVendorCreditsCacheUseCase(this.getCreditsRepository());
    }
    return this.refreshVendorCreditsCacheUseCase;
  }

  // Bank Payments Use Cases
  getBankPaymentsSummary(): GetBankPaymentsSummaryUseCase {
    if (!this.getBankPaymentsSummaryUseCase) {
      this.getBankPaymentsSummaryUseCase = new GetBankPaymentsSummaryUseCase(
        this.getBankPaymentsRepository()
      );
    }
    return this.getBankPaymentsSummaryUseCase;
  }
}

// Export singleton instance
export const recoContainer = new RecoContainer();
